'use client';
import React from 'react';

import Head from 'next/head';

import { useGlobalState } from '@/ui/components/GlobalStateProvider';
import { urlBuilder } from '@/utils/urlBuilder';

type AuthorType = {
  id: number;
  full_name: string;
};

type TranslationDetailsType = {
  id: number;
  slug: string;
  object_title: string;
  title: number;
  content: number;
  translated: number;
  processed: number;
  processed_date: string | null;
  last_original_modified: string;
  last_modified: string;
  updated_pa: string;
  last_push: string;
  pushed_by: AuthorType | number;
  push_is_current: number;
  translation_is_current: number;
};

type TranslationDetailsTypeWithLocale = TranslationDetailsType & {
  locale: string;
};

type TranslationInfoType = {
  [key: string]: TranslationDetailsType;
};

export type LanguageOption = { locale: string; path: string };

interface LanguageOptionsSetterProps {
  item: RestDataPayload;
}

export default function LanguageOptionsSetter({ item }: LanguageOptionsSetterProps) {
  const { setLanguageOptions } = useGlobalState();
  const [contentLanguages, setContentLanguages] = React.useState<LanguageOption[]>([]);

  const addLocale = (translations: TranslationInfoType) => {
    return Object.entries(translations).map(([locale, translation]) => ({
      ...translation,
      locale,
    }));
  };

  const prepareLanguageOptions = (fullyTranslatedItems: TranslationDetailsTypeWithLocale[]) => {
    return fullyTranslatedItems.map(translation => ({
      locale: translation.locale,
      path: urlBuilder({
        item: {
          type: item.type,
          slug: translation?.slug,
          lng: translation.locale,
          parentSlug: item?.parentSlug || null,
        },
        locale: translation.locale,
      }),
    }));
  };

  React.useEffect(() => {
    const translations: TranslationInfoType = item?.translations_info || item?.translations_status;

    if (translations) {
      // transform object to array and move key to locale property
      const translationsWithLocale = addLocale(translations);

      const fullyTranslatedItems = translationsWithLocale.filter(translation => translation.translated);

      const languageOptions = prepareLanguageOptions(fullyTranslatedItems);

      setTimeout(() => {
        setLanguageOptions(languageOptions);
      }, 500);
      setContentLanguages(languageOptions);
    }
  }, [item]);

  const [english, otherLanguages] = React.useMemo(() => {
    const english = contentLanguages.find(({ locale }) => locale === 'en');
    const otherLanguages = contentLanguages.filter(({ locale }) => locale !== 'en');
    return [english, otherLanguages];
  }, [contentLanguages]);

  /**
   * @todo check
   */
  return (
    <Head>
      {otherLanguages.map(({ locale, path }) => (
        <link key={locale} rel="alternate" hrefLang={locale} href={path} />
      ))}
      {english && <link rel="alternate" hrefLang="x-default" href={english.path} />}
    </Head>
  );
}
